import { Box, GlobalStyles, Typography } from "@mui/material";

const LOGO = "/loaderLogo.png";
const NAVY = "#1C3A63";

const LOADER_SIZE = 180;
const BEZEL = 136;
const ARC_R = 58;
const ARC_LEN = 2 * Math.PI * ARC_R;
const DOT_R = 78;
const DOT_GAP = (2 * Math.PI * DOT_R) / 90;

const keyframes = `
@keyframes cl-spin { to { transform: rotate(360deg); } }
@keyframes cl-spin-rev { to { transform: rotate(-360deg); } }
@keyframes cl-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
@media (prefers-reduced-motion: reduce) {
  .cl-anim { animation-duration: 6s !important; }
}
`;

const CustomLoader = ({
  title = "Loading...",
  message = "Please wait while we fetch your data",
}) => (
  <>
    <GlobalStyles styles={keyframes} />

    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(52,80,112,0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      {/* ---------- spinner ---------- */}
      <Box
        sx={{
          position: "relative",
          width: LOADER_SIZE,
          height: LOADER_SIZE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* dotted outer ring */}
        <Box
          className="cl-anim"
          component="svg"
          viewBox="0 0 180 180"
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            animation: "cl-spin-rev 14s linear infinite",
            WebkitMaskImage:
              "conic-gradient(from 0deg, transparent 0deg, #000 110deg, #000 200deg, transparent 300deg)",
            maskImage:
              "conic-gradient(from 0deg, transparent 0deg, #000 110deg, #000 200deg, transparent 300deg)",
          }}
        >
          <circle
            cx="90"
            cy="90"
            r={DOT_R}
            fill="none"
            stroke="#2B4C7E"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={`0 ${DOT_GAP}`}
          />
        </Box>

        {/* glossy bezel */}
        <Box
          sx={{
            position: "absolute",
            width: BEZEL,
            height: BEZEL,
            borderRadius: "50%",
            background: "linear-gradient(145deg, #FFFFFF 0%, #D9E3EE 100%)",
            boxShadow:
              "0 10px 26px rgba(10,30,55,0.30), inset 0 2px 4px rgba(255,255,255,0.95), inset 0 -3px 6px rgba(120,140,165,0.35)",
          }}
        />

        {/* inner face */}
        <Box
          sx={{
            position: "absolute",
            width: 122,
            height: 122,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, #FFFFFF 35%, #E3EEFC 70%, #D2E4FB 100%)",
            boxShadow:
              "inset 0 2px 7px rgba(80,110,150,0.22), 0 0 0 1px rgba(255,255,255,0.7)",
          }}
        />

        {/* rotating navy arcs */}
        <Box
          className="cl-anim"
          component="svg"
          viewBox={`0 0 ${BEZEL} ${BEZEL}`}
          aria-hidden="true"
          sx={{
            position: "absolute",
            width: BEZEL,
            height: BEZEL,
            animation: "cl-spin 1.8s linear infinite",
          }}
        >
          <circle
            cx={BEZEL / 2}
            cy={BEZEL / 2}
            r={ARC_R}
            fill="none"
            stroke="rgba(255,255,255,0.65)"
            strokeWidth="5"
          />
          <circle
            cx={BEZEL / 2}
            cy={BEZEL / 2}
            r={ARC_R}
            fill="none"
            stroke={NAVY}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${ARC_LEN * 0.26} ${ARC_LEN * 0.24}`}
            transform={`rotate(-80 ${BEZEL / 2} ${BEZEL / 2})`}
          />
        </Box>

        {/* logo */}
        <Box
          className="cl-anim"
          component="img"
          src={LOGO}
          alt=""
          sx={{
            position: "relative",
            width: 50,
            height: 50,
            objectFit: "contain",
            animation: "cl-breathe 2.2s ease-in-out infinite",
          }}
        />
      </Box>

      {/* ---------- text ---------- */}
      <Typography
        component="div"
        sx={{
          mt: 0.75,
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.25,
          color: "rgba(255,255,255,0.58)",
        }}
      >
        {title}
      </Typography>

      <Typography
        component="div"
        sx={{
          mt: 0.5,
          maxWidth: 210,
          textAlign: "center",
          fontSize: 12,
          lineHeight: 1.45,
          color: "rgba(255,255,255,0.58)",
        }}
      >
        {message}
      </Typography>
    </Box>
  </>
);

export default CustomLoader;
